import React, { useEffect, useMemo, useState, ReactNode } from "react";

// UX
import CardEditDivisionSupplyOrderUX from "./CardEditDivisionSupplyOrderUX";

// Common
import { Checkbox } from "semantic-ui-react";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import DropDownColumn from "react-lib/appcon/common/DropDownColumn";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Utils
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  SelectedItemsType,
  FilterSearchType,
  TableHeaderType,
  SupplyOrderListSerializer,
} from "./sequence/EditDivisionSupplyOrder";

// Type
type CardEditDivisionSupplyOrderProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  EditDivisionSupplyOrderSequence: State["EditDivisionSupplyOrderSequence"];
  languageUX: "en" | "th";
} & PickedProps;

const CardEditDivisionSupplyOrder = (props: CardEditDivisionSupplyOrderProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<SelectedItemsType[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "EditDivisionSupplyOrder", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  // isChecked
  const isChecked = (id: number) => selectedItems.some((s) => s.id === id);

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      const newData = (props.EditDivisionSupplyOrderSequence?.supplyOrderList || []).map(
        (item: SupplyOrderListSerializer) => ({
          id: item.id,
          request_division: item.order_div,
          perform_division: item.order_perform_div,
        })
      );

      setSelectedItems(newData);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "EditDivisionSupplyOrder",
      action: "SEARCH",
      page: 0,
    });

    setSelectedItems([]);
  };

  const handleSave = () => {
    props.runSequence({
      sequence: "EditDivisionSupplyOrder",
      action: "SAVE",
      selectedItems,
    });

    setSelectedItems([]);
  };

  const handleClear = () => {
    props.runSequence({
      sequence: "EditDivisionSupplyOrder",
      action: "CLEAR",
    });

    setSelectedItems([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;

    props.setProp(`EditDivisionSupplyOrderSequence.searchSupplyOrder.${name}`, data?.value);
  };

  const handleChangeDate = (date: string) => {
    props.setProp(`EditDivisionSupplyOrderSequence.searchSupplyOrder.created_date`, date);
  };

  const handleChangeEdit = (id: number, key: keyof SelectedItemsType, value: any) => {
    setSelectedItems((prev) => {
      const exists = prev.find((s) => s.id === id);

      if (exists) {
        return prev.map((s) => (s.id === id ? { ...s, [key]: value } : s));
      } else {
        const originalItem = props.EditDivisionSupplyOrderSequence?.supplyOrderList?.find(
          (i) => i.id === id
        );

        if (!originalItem) return prev;

        return [
          ...prev,
          {
            ...originalItem,
            [key]: value,
          },
        ];
      }
    });
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`EditDivisionSupplyOrderSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "EditDivisionSupplyOrder",
      action: "SEARCH",
      [key]: value,
    });

    setSelectedItems([]);
    props.setProp(`EditDivisionSupplyOrderSequence.selectedItems`, []);
  };

  const handleDisabledSave = useMemo(() => {
    if (selectedItems.length === 0) return true;
  }, [selectedItems, props.EditDivisionSupplyOrderSequence?.supplyOrderList]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data ?? ""}</div>;
  };

  // EditorColumn Usage
  const renderEditorColumn = (
    id: number,
    field: keyof SelectedItemsType,
    value: any,
    options: any[]
  ) => (
    <DropDownColumn
      options={options}
      value={value}
      showText={value}
      readOnly={!isChecked(id)}
      onDataChanged={(v: any) => handleChangeEdit(id, field, v)}
      backgroundColor={isChecked(id) ? "rgb(255, 255, 204)" : ""}
    />
  );

  const supplyOrderList = useMemo(() => {
    return (props.EditDivisionSupplyOrderSequence?.supplyOrderList || []).map(
      (item: SupplyOrderListSerializer, index: number) => {
        const selectedRow = selectedItems.find((s) => s.id === item.id);
        const requestDivValue = selectedRow?.request_division || item.order_div;
        const performDivValue = selectedRow?.perform_division || item.order_perform_div;

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={isChecked(item.id)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  const exists = selectedItems.find((s) => s.id === item.id);

                  if (!exists) {
                    setSelectedItems([
                      ...selectedItems,
                      {
                        id: item.id,
                        request_division: item.order_div,
                        perform_division: item.order_perform_div,
                      },
                    ]);
                  }
                } else {
                  setSelectedItems(selectedItems.filter((s) => s.id !== item.id));
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof SupplyOrderListSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              if (key === "created") {
                data[key] = columnTableCenter(dateToStringBE(item[key]));
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            },
            {}
          ),
          order_div: renderEditorColumn(
            item.id!,
            "request_division",
            requestDivValue,
            props.masterOptions?.division || []
          ),
          order_perform_div: renderEditorColumn(
            item.id!,
            "perform_division",
            performDivValue,
            props.masterOptions?.division || []
          ),
        };
      }
    );
  }, [
    selectedItems,
    tableHeaders,
    props.EditDivisionSupplyOrderSequence?.supplyOrderList,
    props.masterOptions,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardEditDivisionSupplyOrderUX
        onChangeDate={handleChangeDate}
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchSupplyOrder={props.EditDivisionSupplyOrderSequence?.searchSupplyOrder}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedItems.length ===
                    (props.EditDivisionSupplyOrderSequence?.supplyOrderList || []).length &&
                  (props.EditDivisionSupplyOrderSequence?.supplyOrderList || []).length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        supplyOrderList={supplyOrderList}
        paginatedItems={props.EditDivisionSupplyOrderSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title="Search"
            name="SEARCH"
            size="medium"
          />
        }
        clearButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleClear}
            // data
            paramKey={`${CARD_NAME}_CLEAR`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_CLEAR`]}
            // config
            color="orange"
            title="Clear"
            name="CLEAR"
            size="medium"
          />
        }
        saveButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSave}
            disabled={handleDisabledSave}
            // data
            paramKey={`${CARD_NAME}_SAVE`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SAVE`]}
            // config
            color="green"
            title="Save"
            name="SAVE"
            size="medium"
          />
        }
      />
    </>
  );
};

CardEditDivisionSupplyOrder.displayName = "CardEditDivisionSupplyOrder";

export default React.memo(CardEditDivisionSupplyOrder);

const tableHeaders: TableHeaderType<SupplyOrderListSerializer>[] = [
  {
    Header: "Date",
    accessor: "created",
    width: 150,
  },
  {
    Header: "HN",
    accessor: "hn",
  },
  {
    Header: "Order Code",
    accessor: "code",
  },
  {
    Header: "Type",
    accessor: "type",
    width: 120,
  },
  {
    Header: "Request Division",
    accessor: "order_div",
  },
  {
    Header: "Perform Division",
    accessor: "order_perform_div",
  },
];
